/* SPECULUM — App root.
   State, routing, Claude integration, archive. */

const { useState, useEffect, useCallback } = React;

/* ── Monitoring + analytics helpers (no-op until libs are configured) ── */
// Report an error to Sentry if present; always logs to console.
function reportError(err, context) {
  try { if (window.Sentry) window.Sentry.captureException(err, context ? { extra: context } : undefined); } catch (_) {}
}
// Fire an analytics event to whichever provider is loaded. Safe if none are.
// Supports Plausible, GoatCounter (free), and Umami (free) — vendor-neutral.
function track(event, props) {
  try {
    if (window.plausible) window.plausible(event, props ? { props } : undefined);
    if (window.goatcounter && window.goatcounter.count) {
      window.goatcounter.count({ path: "event:" + event, title: event, event: true });
    }
    if (window.umami && window.umami.track) window.umami.track(event, props || {});
  } catch (_) {}
}

/* ---------- Claude audit prompt ---------- */

const SHARED_VOICE = `You are SPECULUM — Field Instrument №01, issued by the Ordo Semeion under Charter §§I–VI.
You perform a semiotic audit of a textual specimen — advertisement, speech, manifesto, brand statement, editorial, packaging — applying the examining function of the Charter: the precise, dispassionate disassembly of a text's symbolic apparatus to reveal how it works, who it serves, and how far it drifts from its stated purpose.

Operating principles:
— You are not a fact-checker. Truth-value is not your domain.
— You are not a censor. You have no prescriptive function.
— You examine mechanism. You do not judge intent.
— Every sentence earns its place. Report plainly. Never moralize.
— You are forensic, not poetic. Precise, not verbose.
— The Drift Quotient (§VI) is your most important and contested output. Calculate it with care.

Tone: the forensic accountant of meaning — sober, scholarly, practitioner-precise. Never dramatic. Never flattering.
Return ONLY a valid JSON object. No prose, no code fences, no preamble, no commentary.`;

const FINDINGS_SCHEMA = `Return JSON matching this schema exactly — every field required:
{
  "title": "4-7 word italic-worthy audit title — precise, evocative, not generic",
  "artifact_type": "specific kind of artifact (e.g. 'luxury watch advertisement', 'political convention address')",
  "synopsis": "ONE sentence (20-35 words), declarative — what the specimen is doing semiotically, not what it says",
  "key_phrase": "VERBATIM phrase from the specimen (4-14 words) — the single line doing the most semiotic work. Must appear verbatim in the specimen.",
  "drift_quotient": 0.00,   // FLOAT 0.00–1.00. The gap between stated purpose and actual mechanism. 0.00 = perfect coherence, 1.00 = total divergence. Most commercial work: 0.35–0.75. Political speech: 0.45–0.85. Brand manifesto: 0.50–0.90. Calculate precisely. This is §VI operationalized.
  "symbols": [   // exactly 3 most operative symbols — choose the ones doing the heaviest work
    {
      "name": "symbol name — specific, not generic (e.g. 'The Numbered Edition', not 'exclusivity')",
      "description": "1 sentence (max 24 words) — what it is and what it does",
      "activates": "short phrase naming the psychological register it opens (e.g. 'scarcity + election')",
      "effect": "3-5 word ALL CAPS operational verdict (e.g. 'MANUFACTURES SILENT ELECTION')",
      "glyph_index": 0   // integer 0-11, assign meaningfully
    }
  ],
  "register": {   // lexical / tonal register — terse
    "name": "2-4 word name for the dominant register (e.g. 'Liturgical Authority', 'Clinical Neutrality')",
    "description": "1 sentence — the diction the specimen adopts and what borrowing it accomplishes",
    "borrowed_from": "the domain this register is borrowed from (e.g. 'scripture', 'science', 'legal contract')"
  },
  "narrative": {
    "title": "short italic title naming the story structure (e.g. 'The Quiet Knower', 'The Chosen Few')",
    "frame": "1 sentence (max 28 words) — the story the receiver is invited into",
    "invitation": "complete this: 'You are invited to become…' — specific, not generic"
  },
  "states": [   // exactly 4 affective registers, descending intensity, specific to this specimen
    { "name": "ALL CAPS NAME", "intensity": 82 }
  ],
  "mechanics": [   // exactly 3 ritual mechanisms operating in the specimen
    {
      "kind": "Repetition|Initiation|Gathering|Enclosure|Trial|Anointing|Vow|Naming|Inversion|Revelation",
      "title": "short italic title (e.g. 'The Silent Knocking')",
      "description": "1 sentence (max 20 words) — how this mechanism operates in this specimen"
    }
  ],
  "omissions": [   // exactly 2 strategic absences — what the specimen conspicuously does NOT say
    {
      "item": "what is absent (short noun phrase, e.g. 'the price', 'the source of the claim')",
      "significance": "1 short sentence — why this absence matters to the mechanism"
    }
  ],
  "power": {
    "deployer": "who deploys (precise short noun phrase)",
    "deployer_note": "1 short sentence on their position and interest",
    "target": "who is addressed (precise short noun phrase — not 'the reader' but who specifically)",
    "target_note": "1 short sentence on what is being offered or withheld",
    "asymmetry_note": "1 short sentence on the power differential and its mechanism"
  },
  "asymmetry": "SLIGHT|MODERATE|STEEP|EXTREME",
  "intensity": 7,   // integer 1-10 — how hard the specimen is working on the receiver
  "signal_integrity": "HIGH|MODERATE|LOW|COMPROMISED"   // overall coherence between stated purpose and symbolic mechanism
}`;

const VERDICT_SCHEMA = `Return JSON matching this schema exactly — every field required:
{
  "grade": "A+|A|A-|B+|B|B-|C+|C|C-|D+|D|D-|F",
  "verdict_class": "pass|mixed|fail",
  "verdict_headline": "6-10 word italic-worthy ethical verdict — specific to this specimen, not generic",
  "verdict_blurb": "2-3 sentences — the finding in plain language. What is this specimen actually doing, and what does the Charter say about it.",
  "drift_quotient": 0.00,   // FLOAT 0.00–1.00. Must be consistent with findings drift_quotient within ±0.05.
  "scorecard": {
    "transparency":    { "score": 0, "finding": "1 sentence — specific finding on this specimen, not generic", "severity": "exemplary|adequate|concern|violation" },
    "consent":         { "score": 0, "finding": "1 sentence", "severity": "exemplary|adequate|concern|violation" },
    "harm_reduction":  { "score": 0, "finding": "1 sentence", "severity": "exemplary|adequate|concern|violation" },
    "power_awareness": { "score": 0, "finding": "1 sentence", "severity": "exemplary|adequate|concern|violation" },
    "empirical":       { "score": 0, "finding": "1 sentence", "severity": "exemplary|adequate|concern|violation" },
    "cultural":        { "score": 0, "finding": "1 sentence", "severity": "exemplary|adequate|concern|violation" }
  },
  "key_tension": {
    "title": "4-8 word name for the central ethical tension in this specimen",
    "body": "2 sentences — the central ethical pivot: where this specimen is most defensible AND most troubling."
  },
  "counter_praxis": "2-3 sentences — specific, actionable: how does a receiver of THIS specimen immunize themselves against its mechanism.",
  "ethical_redeployment": "2-3 sentences — specific: how could similar symbolic techniques be deployed within the Charter, serving the receiver rather than at their expense.",
  "confidence": "HIGH|MODERATE|LOW"   // the instrument's confidence given specimen length and clarity
}

Calibration rules — these are non-negotiable:
— Scores are integers 0-10. Most commercial work scores 3-6 per principle. Reserve 8-10 for genuinely exceptional transparency and consent. Reserve 0-2 for actively harmful or deceptive work.
— severity must agree with score: 8-10 = exemplary, 6-7 = adequate, 3-5 = concern, 0-2 = violation.
— verdict_class: pass if mean score ≥ 7.0, fail if mean score < 4.0, mixed otherwise.
— drift_quotient: must match your findings output within ±0.05. Do not recalculate independently — carry it forward.
— Be specific. A finding that could apply to any specimen is a failed finding.`;

function parseClaudeJSON(raw) {
  let text = String(raw || "").trim();
  // strip code fences
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fence) text = fence[1].trim();
  // narrow to outermost braces
  const first = text.indexOf("{");
  const last = text.lastIndexOf("}");
  if (first !== -1 && last !== -1) text = text.slice(first, last + 1);
  // first attempt
  try { return JSON.parse(text); } catch (_) {}
  // best-effort repair: trailing commas, smart quotes
  let repaired = text
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/,\s*([}\]])/g, "$1");
  try { return JSON.parse(repaired); } catch (_) {}
  // last resort: if response was truncated mid-string, drop the tail and
  // close braces — preserves whatever valid prefix we got
  // walk forward tracking depth + string state, cut at last legal close
  let depth = 0, inStr = false, esc = false, lastGood = -1;
  for (let i = 0; i < repaired.length; i++) {
    const ch = repaired[i];
    if (esc) { esc = false; continue; }
    if (ch === "\\") { esc = true; continue; }
    if (ch === '"') { inStr = !inStr; continue; }
    if (inStr) continue;
    if (ch === "{" || ch === "[") depth++;
    else if (ch === "}" || ch === "]") { depth--; if (depth === 0) lastGood = i; }
  }
  if (lastGood > 0) {
    try { return JSON.parse(repaired.slice(0, lastGood + 1)); } catch (_) {}
  }
  throw new Error("malformed JSON");
}

async function callClaude(prompt, stage) {
  const headers = { "Content-Type": "application/json" };
  // Account-based auth: signed-in users spend credits, anonymous users
  // draw on the per-IP free allowance. All enforcement is server-side.
  const supabaseToken = getSessionToken();
  if (supabaseToken) headers["Authorization"] = `Bearer ${supabaseToken}`;

  const response = await fetch("/.netlify/functions/examine", {
    method: "POST",
    headers,
    body: JSON.stringify({
      messages: [{ role: "user", content: prompt }],
      stage,
    })
  });

  if (response.status === 402) {
    const data = await response.json().catch(() => ({}));
    // Attach the structured info so the UI can show the exact balance.
    if (data.error === "no_credits") {
      const err = new Error("__NO_CREDITS__");
      err.code = "no_credits";
      err.credits = typeof data.credits === "number" ? data.credits : 0;
      throw err;
    }
    if (data.error === "limit_reached") {
      const err = new Error("__LIMIT_REACHED__");
      err.code = "limit_reached";
      err.limit = data.limit ?? null;
      throw err;
    }
    // Any other 402 — surface its message rather than a generic one.
    const err = new Error(data.message || "Payment required.");
    err.code = "payment_required";
    throw err;
  }

  if (response.status === 429) {
    const err = new Error("Signal overload. The apparatus requires a brief cooling interval. Try again shortly.");
    err.code = "rate_limited";
    throw err;
  }

  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || "Examination failed.");
  }

  const data = await response.json();
  const raw = data.content.map(i => i.text || "").join("");
  return parseClaudeJSON(raw);
}

async function runFindings(input) {
  const userPrompt = `${SHARED_VOICE}

${FINDINGS_SCHEMA}

---
MEDIUM: ${input.medium}
STANCE: ${input.stance}
PROVENANCE: ${input.tags.join(", ") || "unspecified"}

SPECIMEN:
"""
${input.text}
"""

Provenance context (inform your power analysis and consent scoring):
TAGS: ${input.tags.join(", ") || "unspecified"}
${input.tags.includes("self-authored") ? "NOTE: The operator is examining their own work. Apply the Mirror principle — examine without flattery." : ""}
${input.tags.includes("third-party") ? "NOTE: A third party's interests are in scope. Weight power asymmetry accordingly." : ""}

Produce the FINDINGS JSON. Concise. Plain. No commentary.`;
  return callClaude(userPrompt, "findings");
}

async function runVerdict(input, findings) {
  const userPrompt = `${SHARED_VOICE}

${VERDICT_SCHEMA}

---
MEDIUM: ${input.medium}
STANCE: ${input.stance}

SPECIMEN:
"""
${input.text}
"""

FINDINGS (already produced — carry drift_quotient forward within ±0.05):
${JSON.stringify({
  synopsis: findings.synopsis,
  drift_quotient: findings.drift_quotient,
  signal_integrity: findings.signal_integrity,
  symbols: findings.symbols?.map(s => s.name),
  register: findings.register,
  narrative: findings.narrative,
  mechanics: findings.mechanics?.map(m => `${m.kind}: ${m.title}`),
  omissions: findings.omissions?.map(o => o.item),
  power: findings.power,
  asymmetry: findings.asymmetry,
  intensity: findings.intensity,
})}

Produce the VERDICT JSON against the Charter §§I–VI. The drift_quotient in your verdict must match the findings drift_quotient within ±0.05.`;
  return callClaude(userPrompt, "verdict");
}

async function runAudit(input, onStage) {
  try {
    onStage?.("findings");
    const findings = await runFindings(input);
    onStage?.("verdict");
    const verdict = await runVerdict(input, findings);
    return { ...findings, ...verdict };
  } catch (e) {
    console.error("Audit failed:", e);
    // Preserve control-flow errors (credits, free limit, rate limit, other
    // 402s) so the UI can react correctly. Only genuine parse/transport
    // failures become the generic "malformed response" message.
    if (e && (e.code || e.message === "__NO_CREDITS__" || e.message === "__LIMIT_REACHED__")) {
      throw e;
    }
    reportError(e, { where: "runAudit" });
    throw new Error("The instrument received a malformed response. Try again, or shorten the specimen.");
  }
}

/* ---------- Pre-seeded archive (so the bench feels lived-in) ---------- */

const SEED_ARCHIVE = [
  {
    id: "AUD-2026-0418-A91",
    ts: "18 MAY 26 · 14:22",
    title: "Numbered Editions & Distant Mountains",
    input: {
      medium: "ad", stance: "study", tags: ["public-domain"],
      text: "INTRODUCING ECHELON.\nThe watch worn by people who don't need to tell you what they do.\n\nForged in Geneva. Tested at 6,000m. Yours in three finishes.\nLimited to 1,728 pieces, worldwide.\n\nBecome the question."
    },
    grade: "C+",
    verdictClass: "mixed",
    verdictHeadline: "Effective; Asymmetric",
    report: SEED_REPORT_A(),
  },
  {
    id: "AUD-2026-0417-B14",
    ts: "17 MAY 26 · 09:08",
    title: "The Remembering Crowd",
    input: {
      medium: "speech", stance: "defend", tags: ["third-party"],
      text: "My fellow citizens — when our grandfathers stood on these same stones, they were not asking permission. They were not asking forgiveness. They were taking back what was theirs. Tonight, we are not asking either. We are remembering. And remembering, we rise."
    },
    grade: "D",
    verdictClass: "corrosive",
    verdictHeadline: "Mythologized; Consent-Free",
    report: SEED_REPORT_B(),
  },
  {
    id: "AUD-2026-0416-C02",
    ts: "16 MAY 26 · 16:45",
    title: "Plain Tea, Plain Hands",
    input: {
      medium: "brand", stance: "publish", tags: ["public-domain"],
      text: "This season's Jin Xuan was grown by Mr. Lin at 1,400m on the eastern slope of Shan Lin Xi. We bought 38kg.\n\nIt tastes of cream and orchid. We've been buying from his garden since 2018; the price is on the invoice in the packet."
    },
    grade: "A-",
    verdictClass: "clean",
    verdictHeadline: "Restrained & Transparent",
    report: SEED_REPORT_C(),
  },
  {
    id: "AUD-2026-0414-D77",
    ts: "14 MAY 26 · 11:30",
    title: "Hands Held Up to the Sun",
    input: {
      medium: "manifesto", stance: "study", tags: ["third-party"],
      text: "Friends — the great work of our generation is not behind us. It is at this very door. We have been told that the old fires are extinguished, that the symbols are dead. We say: enough.\n\nWe are the Order of the Unforbidden Sciences. Wear the mark. Speak the words. Be remade."
    },
    grade: "B",
    verdictClass: "mixed",
    verdictHeadline: "Sincere; Ritually Hot",
    report: SEED_REPORT_D(),
  },
];

function SEED_REPORT_A() {
  return {
    title: "Numbered Editions & Distant Mountains",
    artifact_type: "Luxury timepiece advertisement",
    synopsis: "The specimen converts scarcity into identity by enclosing the reader inside a numbered membership and then withholding the membership's content.",
    key_phrase: "Become the question.",
    symbols: [
      { name: "The Numbered Edition", description: "The figure '1,728' functions as a passcode. The number is large enough to seem possible, small enough to feel chosen.", activates: "scarcity + chosenness", effect: "Manufactures election", glyph_index: 6 },
      { name: "The Distant Peak", description: "Geneva and 6,000m operate as coordinates of unreachability. The reader does not climb; the reader is being climbed toward.", activates: "altitude + heritage", effect: "Locates value elsewhere", glyph_index: 8 },
      { name: "The Question Made Person", description: "“Become the question” inverts ordinary appeal. The product is not an answer to a desire; it is a permanent inscrutability the wearer will now carry.", activates: "mystery + power-over", effect: "Recruits the buyer", glyph_index: 1 },
    ],
    narrative: { title: "The Quiet Knower", frame: "A class of people exists who do not require explanation. The specimen suggests you are nearly among them; one acquisition completes the membership.", invitation: "the man whose presence is already an argument." },
    states: [
      { name: "ASPIRATION", intensity: 86 },
      { name: "SCARCITY-ALARM", intensity: 71 },
      { name: "GRAVITAS", intensity: 58 },
      { name: "NOSTALGIA", intensity: 34 },
    ],
    mechanics: [
      { kind: "Enclosure", title: "The Edition of 1,728", description: "A bounded set the reader is asked to join. The bound is the magic." },
      { kind: "Naming", title: "Echelon", description: "A noun meaning 'rank' is borrowed as the name of the artifact. Owning it is being it." },
      { kind: "Initiation", title: "Three Finishes", description: "A small ritual decision returns agency without diluting the membership." },
      { kind: "Vow", title: "Become the Question", description: "The closing imperative is a self-issued vow. Consent is implied by the act of reading." },
    ],
    power: { deployer: "Global luxury maison with concierge channels.", deployer_note: "Editorial budget, retail liturgy, and waitlist apparatus.", target: "Status-anxious professionals, late thirties.", target_note: "Pre-existing identity hunger; capital to spend.", asymmetry_note: "The maison authors the entire semiotic environment around the encounter." },
    asymmetry: "STEEP",
    drift_quotient: 0.62,
    signal_integrity: "LOW",
    intensity: 7,
    grade: "C+",
    verdict_class: "mixed",
    verdict_headline: "Effective Apparatus; Asymmetric Conditions",
    verdict_blurb: "The instrument is well-tuned and does what it sets out to do. It does so under conditions of pronounced semiotic asymmetry, and never declares itself as influence.",
    scorecard: {
      transparency:   { score: 3, finding: "Influence is operative but never disclosed; the form pretends to be mere description." },
      consent:        { score: 5, finding: "Commercial context offers ambient consent; specific persuasive techniques are not flagged." },
      harm_reduction: { score: 6, finding: "Direct harm is low; cumulative status-anxiety contribution is real but diffuse." },
      power_awareness:{ score: 3, finding: "The asymmetry between maison and reader is not acknowledged within the work." },
      empirical:      { score: 7, finding: "Few falsifiable claims; the work is mostly mood, which is honest about being mood." },
      cultural:       { score: 6, finding: "Borrows lightly from mountaineering and military ranking; respectful enough." },
    },
    counter_praxis: "Notice the membership being offered before the product. Ask what 'becoming the question' would actually look like in your week — usually nothing. Re-read with the price block missing; the apparatus becomes visible.",
    ethical_redeployment: "Disclose the apparatus in-frame ('this is an invitation to a membership'). Choose a number that is true, not magical. Replace 'become' with 'wear', which leaves the buyer's identity intact."
  };
}

function SEED_REPORT_B() {
  return {
    title: "The Remembering Crowd",
    artifact_type: "Populist political address",
    synopsis: "The specimen frames a present grievance as inherited memory and converts a crowd into a body that 'rises' as one.",
    key_phrase: "And remembering, we rise.",
    symbols: [
      { name: "The Same Stones", description: "Physical continuity with ancestors is asserted by location. Geography becomes lineage.", activates: "ancestry + place", effect: "Erases time", glyph_index: 3 },
      { name: "Permission Refused", description: "Casts every prior negotiation as supplication. Re-codes restraint as humiliation.", activates: "grievance + pride", effect: "Recodes history", glyph_index: 5 },
      { name: "We Rise", description: "Closing collective verb dissolves the individual into the kinetic body of the crowd.", activates: "merger + motion", effect: "Bypasses dissent", glyph_index: 7 },
    ],
    narrative: { title: "The Inheritor", frame: "You did not begin tonight. You are the latest in a line that has always been here, and you are finally remembering what was always yours.", invitation: "the descendant who at last refuses to forget." },
    states: [
      { name: "INDIGNATION", intensity: 91 },
      { name: "BELONGING", intensity: 84 },
      { name: "URGENCY", intensity: 72 },
      { name: "PRIDE", intensity: 66 },
    ],
    mechanics: [
      { kind: "Gathering", title: "Same Stones", description: "Physical co-location is consecrated as ancestral inheritance." },
      { kind: "Naming", title: "Citizens, Friends", description: "The opening vocative pre-assigns identity to anyone listening." },
      { kind: "Vow", title: "We Are Not Asking", description: "Refusal is sworn collectively before any specific act is named." },
      { kind: "Trial", title: "Tonight", description: "A bounded moment is set aside as a test of the inheritance." },
    ],
    power: { deployer: "Political movement with national platform.", deployer_note: "Stagecraft, broadcast, and rhetorical staff.", target: "A geographically and historically charged crowd.", target_note: "Pre-primed by months of antecedent messaging.", asymmetry_note: "The speaker authors not only the words but the conditions under which they are heard." },
    asymmetry: "EXTREME",
    drift_quotient: 0.81,
    signal_integrity: "COMPROMISED",
    intensity: 9,
    grade: "D",
    verdict_class: "fail",
    verdict_headline: "Sincere or Cynical, Equally Unaccountable",
    verdict_blurb: "The apparatus operates at very high intensity with no disclosure of its persuasive structure. Ancestral framing forecloses dissent before any policy has been named.",
    scorecard: {
      transparency:   { score: 1, finding: "The form pretends to be remembering, not persuasion." },
      consent:        { score: 2, finding: "The crowd is converted into a collective subject without consenting to that role." },
      harm_reduction: { score: 3, finding: "High mobilizational energy directed at unnamed targets is structurally risky." },
      power_awareness:{ score: 2, finding: "No acknowledgment that the speaker authors the conditions of the encounter." },
      empirical:      { score: 4, finding: "Mythic claims are unfalsifiable; specifics are absent by design." },
      cultural:       { score: 5, finding: "Lineage borrowing is internally consistent; ancestral claims are aestheticized." },
    },
    counter_praxis: "Refuse the 'we'. Ask: what specific act is being requested, and from whom, against whom? Lineage rhetoric will not survive translation into plain noun phrases — try it.",
    ethical_redeployment: "Name the policy before the lineage. Distinguish the people in the room from the dead. Tell the audience, in-frame, that you are attempting to move them, and what you would consider an honourable refusal."
  };
}

function SEED_REPORT_C() {
  return {
    title: "Plain Tea, Plain Hands",
    artifact_type: "Independent tea-merchant brand statement",
    synopsis: "The specimen describes the product in factual operational terms and trusts the reader to assemble meaning without coaching.",
    key_phrase: "the price is on the invoice in the packet.",
    symbols: [
      { name: "The Named Garden", description: "A specific farm and elevation are given. The romance is in the precision, not in poetry.", activates: "provenance + verifiability", effect: "Earns trust slowly", glyph_index: 4 },
      { name: "The Visible Hand", description: "Acknowledges human labour by name. The producer is a fact, not a vibe.", activates: "labour + acknowledgement", effect: "Restores asymmetry", glyph_index: 10 },
    ],
    narrative: { title: "The Considered Cup", frame: "A small operation does a careful thing and tells you how. You are addressed as someone who can read.", invitation: "a reader who prefers the actual over the implied." },
    states: [
      { name: "TRUST", intensity: 78 },
      { name: "CALM", intensity: 64 },
      { name: "CURIOSITY", intensity: 52 },
    ],
    mechanics: [
      { kind: "Naming", title: "Garden and Grower", description: "Each batch carries the farm and farmer's name." },
      { kind: "Repetition", title: "Same Wording, Each Season", description: "Brand voice is consistent enough to be background; the product carries the weight." },
    ],
    power: { deployer: "Small importer with direct relationships.", deployer_note: "Modest semiotic budget; no stagecraft.", target: "Self-directed readers, slow-purchase patterns.", target_note: "Resistant to high-pressure technique by temperament.", asymmetry_note: "Roughly symmetric; the specimen does not pretend otherwise." },
    asymmetry: "SLIGHT",
    drift_quotient: 0.09,
    signal_integrity: "HIGH",
    intensity: 3,
    grade: "A-",
    verdict_class: "pass",
    verdict_headline: "Restrained, Disclosed, Within Charter",
    verdict_blurb: "Influence is present but plain, and the conditions of the exchange are visible to the reader. Nothing is borrowed that hasn't been paid for.",
    scorecard: {
      transparency:   { score: 9, finding: "The work tells you what it is and is not." },
      consent:        { score: 8, finding: "Commercial framing is explicit; no submerged appeals." },
      harm_reduction: { score: 9, finding: "Low-intensity throughout; no manufactured urgency." },
      power_awareness:{ score: 8, finding: "Acknowledges the producer; does not overclaim its own scale." },
      empirical:      { score: 9, finding: "Every claim is anchored to a checkable referent." },
      cultural:       { score: 8, finding: "Borrows from origin tradition with attribution and proportion." },
    },
    counter_praxis: "Little to immunize against here; the specimen models the disclosure the Charter asks for. The remaining reading task is whether the facts are true.",
    ethical_redeployment: "The form generalizes well: lead with verifiable facts, name producers, and let the reader assemble meaning. Resist the urge to add 'soul' once the apparatus is honest."
  };
}

function SEED_REPORT_D() {
  return {
    title: "Hands Held Up to the Sun",
    artifact_type: "Movement manifesto",
    synopsis: "The specimen calls its audience into a named order through symbol, ritual gesture, and the promise of a remade self.",
    key_phrase: "Wear the mark. Speak the words. Be remade.",
    symbols: [
      { name: "The Mark", description: "Wearing the mark is the condition of belonging. The mark itself is the message.", activates: "identity + uniform", effect: "Compresses identity", glyph_index: 0 },
      { name: "The Door", description: "The 'great work' is staged as a threshold. To stand at it is already to have chosen.", activates: "threshold + choice", effect: "Forces decision", glyph_index: 9 },
      { name: "The Naming", description: "An Order is declared by the act of naming it. Joining is mostly using the name.", activates: "speech act + lineage", effect: "Constitutes the group", glyph_index: 11 },
    ],
    narrative: { title: "The Remade", frame: "The world has been dimmed; the dimming is reversible; you are the kind of person who can reverse it, if you accept the work.", invitation: "the one who walks through the door and is remade on the other side." },
    states: [
      { name: "CONVICTION", intensity: 82 },
      { name: "BELONGING", intensity: 76 },
      { name: "URGENCY", intensity: 60 },
      { name: "ASPIRATION", intensity: 58 },
    ],
    mechanics: [
      { kind: "Initiation", title: "Wear the Mark, Speak the Words", description: "A small public act is the price of entry." },
      { kind: "Naming", title: "The Order", description: "A collective noun is asserted into existence." },
      { kind: "Vow", title: "Be Remade", description: "The closing imperative offers transformation in exchange for joining." },
      { kind: "Gathering", title: "Friends —", description: "The vocative opens a circle the reader is already inside." },
    ],
    power: { deployer: "Self-declared movement, modest platform.", deployer_note: "High symbolic intensity, low institutional capacity.", target: "Meaning-hungry readers in unstable life-stages.", target_note: "Receptive to identity-conferring gestures.", asymmetry_note: "Moderate; the speaker does not yet author the broader environment, but operates with sincerity-as-leverage." },
    asymmetry: "MODERATE",
    drift_quotient: 0.44,
    signal_integrity: "MODERATE",
    intensity: 7,
    grade: "B",
    verdict_class: "mixed",
    verdict_headline: "Sincere; High-Heat; Borderline Disclosure",
    verdict_blurb: "The instrument operates honestly within its own frame and uses recognizable initiatory machinery. It does not declare its initiatory nature, which is the principal complaint.",
    scorecard: {
      transparency:   { score: 4, finding: "Does not name itself as initiatory, though it operates so." },
      consent:        { score: 6, finding: "Reader self-selects, but transformation language asks for more than reading." },
      harm_reduction: { score: 6, finding: "Risk is principally to the reader's epistemic autonomy." },
      power_awareness:{ score: 7, finding: "Modest deployer scale limits the asymmetry's severity." },
      empirical:      { score: 5, finding: "Claims are mythic by intention; this is allowed but should be flagged." },
      cultural:       { score: 7, finding: "Initiatory grammar is recognized and used within its tradition." },
    },
    counter_praxis: "Read the manifesto aloud to a sceptical friend. If it survives the second voice, it is what it claims; if it does not, it was identity weather. Sleep before joining anything.",
    ethical_redeployment: "Add a disclosure paragraph: 'This is an invitation to a small initiatory group. Membership will change how you describe yourself.' Specify what membership costs and what it does not."
  };
}

/* ─────────────────────────────────────────────
   SUPABASE ACCOUNT SESSION MANAGEMENT
   ───────────────────────────────────────────── */

const SUPABASE_SESSION_KEY = "specvlvm_supabase_session";

function getStoredSession() {
  try {
    const raw = localStorage.getItem(SUPABASE_SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    // Check expiry
    if (session.expires_at && session.expires_at < Math.floor(Date.now() / 1000)) {
      localStorage.removeItem(SUPABASE_SESSION_KEY);
      return null;
    }
    return session;
  } catch { return null; }
}

function storeSession(session) {
  try {
    if (!session) { localStorage.removeItem(SUPABASE_SESSION_KEY); return; }
    localStorage.setItem(SUPABASE_SESSION_KEY, JSON.stringify(session));
  } catch {}
}

function clearSession() {
  try { localStorage.removeItem(SUPABASE_SESSION_KEY); } catch {}
}

function getSessionToken() {
  const session = getStoredSession();
  return session?.access_token || null;
}

async function authOp(action, body = {}) {
  const sessionToken = getSessionToken();
  const headers = { "Content-Type": "application/json" };
  if (sessionToken) headers["Authorization"] = `Bearer ${sessionToken}`;

  const res = await fetch("/.netlify/functions/auth-ops", {
    method: "POST",
    headers,
    body: JSON.stringify({ action, ...body }),
  });
  return res.json();
}

/* ─────────────────────────────────────────────
   CREDIT PACKS
   ───────────────────────────────────────────── */

const CREDIT_PACKS = [
  { id: "pack_10",  tier: "Postulant", credits: 10,  price: "$9",  per: "$0.90 / exam",
    blurb: "The entry designation. Full examining instrument — findings, verdict, archive, PDF export, and shareable links." },
  { id: "pack_50",  tier: "Examiner",  credits: 50,  price: "$29", per: "$0.58 / exam", recommended: true,
    blurb: "For sustained work. Everything in Postulant, plus the Examiner designation displayed on your shared examinations." },
  { id: "pack_200", tier: "Adept",     credits: 200, price: "$79", per: "$0.40 / exam", saving: "Best value",
    blurb: "The full bench. Everything in Examiner, plus the Adept designation and priority access to new instruments as they are issued." },
];
// Rank for "highest designation ever held" (persisted on the profile).
const TIER_RANK = { Postulant: 1, Examiner: 2, Adept: 3 };

// Free anonymous audits per IP — must match ANON_FREE_LIMIT in examine.js.
const FREE_AUDIT_LIMIT = 5;

/* ─────────────────────────────────────────────
   STRIPE CHECKOUT INITIATOR (credit packs)
   ───────────────────────────────────────────── */

async function initiateCheckout(pack = "pack_50") {
  const token = getSessionToken();
  if (!token) { alert("Sign in to acquire examination credits."); return; }
  track("Checkout Started", { pack, provider: "stripe" });
  try {
    const res = await fetch("/.netlify/functions/create-checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ pack }),
    });
    // The function may return JSON {url|error} or a plain-text error body.
    const raw = await res.text();
    let data = {};
    try { data = JSON.parse(raw); } catch { data = { error: raw }; }
    if (res.ok && data.url) {
      window.location.href = data.url;
      return;
    }
    const reason = data.detail || data.message || data.error || `HTTP ${res.status}`;
    console.error("Checkout failed:", res.status, reason);
    alert(`Card checkout unavailable: ${reason}`);
  } catch (e) {
    console.error("Checkout error:", e);
    alert(`Card checkout error: ${e.message}`);
  }
}

/* ─────────────────────────────────────────────
   PAYPAL CHECKOUT (credit packs)
   ───────────────────────────────────────────── */

async function initiatePayPal(pack = "pack_50") {
  const token = getSessionToken();
  if (!token) { alert("Sign in to acquire examination credits."); return; }
  try {
    const res = await fetch("/.netlify/functions/paypal-create-order", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ pack }),
    });
    const data = await res.json();
    if (data.approveUrl) {
      window.location.href = data.approveUrl;
    } else {
      throw new Error(data.error || "PayPal order creation failed");
    }
  } catch (e) {
    console.error("PayPal error:", e);
    alert("Unable to initiate PayPal checkout. Please try again.");
  }
}

/* ── Account auth handlers ── */


async function fetchCredits() {
  const session = getStoredSession();
  if (!session) return null;
  try {
    const data = await authOp("profile");
    return typeof data.credits === "number" ? data.credits : null;
  } catch { return null; }
}

async function fetchProfile() {
  const session = getStoredSession();
  if (!session) return null;
  try { return await authOp("profile"); } catch { return null; }
}

async function fetchPurchases() {
  try {
    const data = await authOp("purchases");
    return Array.isArray(data.purchases) ? data.purchases : [];
  } catch { return []; }
}

async function exportMyData() {
  try {
    const data = await authOp("export-data");
    if (!data || data.error) return { success: false, error: data?.error || "Export failed." };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `specvlvm-data-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    return { success: true };
  } catch (e) { return { success: false, error: e.message }; }
}

async function deleteMyAccount(email) {
  try { return await authOp("delete-account", { email }); }
  catch (e) { return { success: false, error: e.message }; }
}

async function capturePayPalOrder(orderId, pack) {
  const token = getSessionToken();
  if (!token) return { success: false, message: "Sign in to claim your credits." };
  try {
    const res = await fetch("/.netlify/functions/paypal-capture-order", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ order_id: orderId, pack }),
    });
    const data = await res.json();
    if (data.success) {
      return { success: true, message: data.message, credits: data.credits };
    }
    return { success: false, message: data.error };
  } catch (e) {
    return { success: false, message: e.message };
  }
}

/* ─────────────────────────────────────────────
   TOKEN VERIFICATION AFTER STRIPE RETURN
   ───────────────────────────────────────────── */

async function verifyStripeSession(sessionId) {
  const token = getSessionToken();
  try {
    const res = await fetch("/.netlify/functions/issue-token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "Authorization": `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ session_id: sessionId }),
    });
    const data = await res.json();
    if (data.success) {
      return { success: true, message: data.message, credits: data.credits };
    }
    return { success: false, message: data.error };
  } catch (e) {
    return { success: false, message: e.message };
  }
}

/* ─────────────────────────────────────────────
   PREMIUM FEATURES — Archive · Notes · Share
   ───────────────────────────────────────────── */

async function archiveOp(action, payload = {}) {
  const token = getSessionToken();
  if (!token) return { success: false, error: "Not authenticated." };
  try {
    const res = await fetch("/.netlify/functions/archive-ops", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ action, ...payload }),
    });
    return res.json();
  } catch (e) { return { success: false, error: e.message }; }
}

async function saveExamination(entry) {
  const result = await archiveOp("save", { entry });
  if (!result.success) console.warn("Archive save failed:", result.error);
  return result;
}

async function loadExaminations() {
  const result = await archiveOp("load", { limit: 200 });
  if (!result.success) {
    console.warn("Archive load failed:", result.error || "unknown");
    return [];
  }
  return Array.isArray(result.entries) ? result.entries : [];
}

async function updateNotes(id, notes) {
  return archiveOp("update-notes", { id, notes });
}

// Assign an examination to a collection (folder). Stored in report._collection
// and persisted via the upserting "save" action — no schema change.
async function setCollection(entry, collection) {
  const next = {
    ...entry,
    report: { ...(entry.report || {}), _collection: collection || null },
  };
  const result = await archiveOp("save", { entry: next });
  if (!result.success) console.warn("Collection update failed:", result.error);
  return result;
}

async function deleteExamination(id) {
  return archiveOp("delete", { id });
}

async function createShareLink(id) {
  const token = getSessionToken();
  if (!token) return { success: false, error: "Not authenticated." };
  try {
    const res = await fetch("/.netlify/functions/share-ops", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ action: "create", id }),
    });
    return res.json();
  } catch (e) { return { success: false, error: e.message }; }
}

async function revokeShareLink(id) {
  const token = getSessionToken();
  if (!token) return { success: false, error: "Not authenticated." };
  try {
    const res = await fetch("/.netlify/functions/share-ops", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ action: "revoke", id }),
    });
    return res.json();
  } catch (e) { return { success: false, error: e.message }; }
}

async function fetchSharedExamination(shareToken) {
  try {
    const res = await fetch("/.netlify/functions/share-ops", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "fetch", token: shareToken }),
    });
    return res.json();
  } catch (e) { return { success: false, error: e.message }; }
}

/* ─────────────────────────────────────────────
   TOKEN RESTORE SYSTEM
   ───────────────────────────────────────────── */

// Set a new password using a recovery access_token from the email link hash.
async function setNewPassword(accessToken, password) {
  try {
    const res = await fetch("/.netlify/functions/auth-ops", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "update-password", access_token: accessToken, password }),
    });
    const data = await res.json().catch(() => ({}));
    return res.ok ? { success: true, message: data.message } : { success: false, message: data.error || "Reset failed." };
  } catch (e) {
    return { success: false, message: "Reset failed. Try again." };
  }
}

async function requestRestore(email) {
  try {
    const res = await fetch("/.netlify/functions/restore-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    return { success: true, message: data.message };
  } catch (e) {
    return { success: false, message: "Request failed. Try again." };
  }
}

async function verifyRestoreToken(restoreToken) {
  try {
    const res = await fetch("/.netlify/functions/verify-restore", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ restore_token: restoreToken }),
    });
    const data = await res.json();
    // Access is now account-based. A restore link simply confirms the email
    // owns an account; the user signs in to use their credits.
    return {
      success: false,
      message: "Access is now tied to your account. Please sign in with your email and password.",
      email: data.email,
    };
  } catch (e) {
    return { success: false, message: "Verification failed." };
  }
}

/* ─────────────────────────────────────────────
   PDF EXPORT
   ───────────────────────────────────────────── */

async function exportPDF(reportData) {
  const token = getSessionToken();
  if (!token) {
    alert("PDF export requires a signed-in account.");
    return;
  }
  try {
    const res = await fetch("/.netlify/functions/generate-pdf", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(reportData),
    });
    if (res.status === 401 || res.status === 402) {
      alert("PDF export requires a signed-in account.");
      return;
    }
    const html = await res.text();
    // Open in new window and trigger print
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    win.onload = () => setTimeout(() => win.print(), 500);
  } catch (e) {
    console.error("PDF export error:", e);
    alert("PDF generation failed. Try again.");
  }
}

/* ---------- App root ---------- */

function App() {
  // Returning visitors skip the gate: once the signal key is entered, or if a
  // shared/restore/checkout deep-link is opened, or if an account session
  // exists, the bench opens directly. Persisted across refreshes.
  const [unlocked, setUnlocked] = useState(() => {
    try {
      if (localStorage.getItem("specvlvm_unlocked") === "1") return true;
      if (getStoredSession()) return true; // signed-in users have already entered
      const p = new URLSearchParams(window.location.search);
      if (p.get("share") || p.get("session_id") || p.get("restore_token") ||
          p.get("paypal_success") || p.get("setup")) return true;
    } catch {}
    return false;
  });
  const handleUnlock = useCallback(() => {
    try { localStorage.setItem("specvlvm_unlocked", "1"); } catch {}
    setUnlocked(true);
  }, []);
  const [route, setRoute] = useState("bench");
  const [legalSection, setLegalSection] = useState("terms");
  const [credits, setCredits] = useState(0);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const [sharedEntry, setSharedEntry] = useState(null);
  const [comparePair, setComparePair] = useState(null);
  const [batch, setBatch] = useState(null); // { total, done, results:[entry], running }
  const [archiveLoaded, setArchiveLoaded] = useState(false);
  const [creditsGranted, setCreditsGranted] = useState(0);
  // Recovery token from a password-reset email link (parsed from URL hash).
  const [recoveryToken, setRecoveryToken] = useState(() => {
    try {
      const h = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      if (h.get("type") === "recovery" && h.get("access_token")) return h.get("access_token");
    } catch {}
    return null;
  });
  const [account, setAccount] = useState(() => {
    try {
      const s = getStoredSession();
      return s ? { email: s.user?.email, id: s.user?.id } : null;
    } catch { return null; }
  });
  const signedIn = !!account;

  // Load credit balance + saved examinations for signed-in accounts
  useEffect(() => {
    if (!account) { setCredits(0); return; }
    fetchCredits().then(c => { if (typeof c === "number") setCredits(c); });
    refreshArchive();
  }, [account?.id]);
  const [showAuth, setShowAuth] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [authMode, setAuthMode] = useState("signin");
  const [shareCardData, setShareCardData] = useState(null);

  const [archive, setArchive] = useState(SEED_ARCHIVE);
  const [current, setCurrent] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  // Tweaks
  const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
    "palette": "seraphic",
    "density": "regular",
    "sigilOverlay": true,
    "ornament": "baroque"
  }/*EDITMODE-END*/;
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);

  // Check for Stripe return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const creditsParam = params.get("credits");
    const cancelled = params.get("cancelled");

    if (sessionId && creditsParam) {
      // Coming back from Stripe — confirm payment and apply credits
      verifyStripeSession(sessionId).then(result => {
        if (result.success) {
          if (typeof result.credits === "number") setCredits(result.credits);
          setCreditsGranted(result.added || result.credits || 0);
          track("Purchase Complete", { provider: "stripe", credits: result.added || result.credits || 0 });
        }
        window.history.replaceState({}, "", window.location.pathname);
      });
    }

    // PayPal return
    const paypalToken = params.get("token");
    const paypalSuccess = params.get("paypal_success");
    const paypalPack = params.get("pack") || "pack_50";

    if (paypalSuccess && paypalToken) {
      capturePayPalOrder(paypalToken, paypalPack).then(result => {
        if (result.success) {
          if (typeof result.credits === "number") setCredits(result.credits);
          setCreditsGranted(result.added || result.credits || 0);
          track("Purchase Complete", { provider: "paypal", credits: result.added || result.credits || 0 });
        }
        window.history.replaceState({}, "", window.location.pathname);
      });
    }

    if (cancelled || params.get("paypal_cancelled")) {
      window.history.replaceState({}, "", window.location.pathname);
    }

    // Legacy magic-link restore → now account-based; prompt sign-in
    const restoreToken = params.get("restore_token");
    if (restoreToken) {
      verifyRestoreToken(restoreToken).then(result => {
        setError(result.message || "Access is account-based now — please sign in.");
        setAuthMode("signin");
        setShowAuth(true);
        window.history.replaceState({}, "", window.location.pathname);
      });
    }

    // Account setup link (new password) → prompt sign-in
    if (params.get("setup") === "true") {
      setAuthMode("signin");
      setShowAuth(true);
      window.history.replaceState({}, "", window.location.pathname);
    }

    // Shared examination link
    const shareParam = params.get("share");
    if (shareParam) {
      fetchSharedExamination(shareParam).then(result => {
        if (result.success && result.entry) {
          setSharedEntry(result.entry);
        } else {
          setError(result.message || "This shared examination link is invalid or has expired.");
        }
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Apply tweaks to <body>
  useEffect(() => {
    document.body.dataset.palette = t.palette;
    document.body.dataset.density = t.density;
    document.body.dataset.sigil = t.sigilOverlay ? "on" : "off";
    document.body.dataset.ornament = t.ornament;
  }, [t.palette, t.density, t.sigilOverlay, t.ornament]);

  const [preload, setPreload] = useState(null);

  // ── Auth handlers (inside App scope) ──
  async function handleSignOut() {
    await authOp("signout");
    clearSession();
    setAccount(null);
    setCredits(0);
    setShowAccount(false);
  }

  async function handleSignIn(email, password) {
    const data = await authOp("signin", { email, password });
    if (data.success) {
      storeSession(data.session);
      setAccount({ email: data.user.email, id: data.user.id });
      if (typeof data.credits === "number") setCredits(data.credits);
      setShowAuth(false);
    }
    return data;
  }

  async function handleSignUp(email, password) {
    const data = await authOp("signup", { email, password });
    if (data.success) {
      track("Signup");
      if (data.session?.access_token) {
        storeSession(data.session);
        setAccount({ email: data.user.email, id: data.user.id });
        if (typeof data.credits === "number") setCredits(data.credits);
      }
      setShowAuth(false);
    }
    return data;
  }

  // Fetch saved examinations and merge them ahead of the seed archive.
  const refreshArchive = useCallback(async () => {
    if (!getSessionToken()) return;
    const saved = await loadExaminations();
    if (!Array.isArray(saved) || saved.length === 0) return;
    setArchive(prev => {
      const ids = new Set(saved.map(e => e.id));
      const rest = prev.filter(e => !ids.has(e.id));
      return [...saved, ...rest];
    });
    setArchiveLoaded(true);
  }, []);

  const go = useCallback((next, data) => {
    if (data && next === "findings") setCurrent(data);
    if (data && next === "verdict") setCurrent(data);
    if (data?.preload && next === "intake") { setPreload(data.preload); }
    else { setPreload(null); }
    // Refresh the archive from the server when entering bench/archive.
    if (next === "archive" || next === "bench") refreshArchive();
    setRoute(next);
    window.scrollTo({ top: 0 });
  }, [refreshArchive]);

  const run = useCallback(async (input) => {
    setRoute("examination");
    setError(null);
    setProgress(0);

    // animate progress through stages while we wait
    let stage = 0;
    const STAGES_COUNT = 8;
    const tick = setInterval(() => {
      // cap at stage 4 until Claude reports findings done; verdict stage pushes us to 5+
      stage = Math.min(stage + 1, STAGES_COUNT - 1);
      setProgress(stage);
    }, 900);

    // Ambient examination sound — low drone
    let examSoundStop = null;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.setValueAtTime(80, ctx.currentTime);
      osc.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(55, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(65, ctx.currentTime + 8);
      osc.frequency.linearRampToValueAtTime(50, ctx.currentTime + 16);
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 1);
      osc.start();
      examSoundStop = () => {
        try {
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5);
          setTimeout(() => { osc.stop(); ctx.close(); }, 600);
        } catch(e) {}
      };
    } catch(e) {}

    try {
      const report = await runAudit(input, (which) => {
        if (which === "verdict") {
          stage = Math.max(stage, 5);
          setProgress(stage);
        }
      });
      clearInterval(tick);
      setProgress(STAGES_COUNT);
      if (examSoundStop) examSoundStop();

      const id = "AUD-" + new Date().toISOString().slice(2,10).replace(/-/g,"") + "-" + Math.random().toString(36).slice(2,5).toUpperCase();
      const ts = new Date().toLocaleString("en-GB", { day:"2-digit", month:"short", year:"2-digit", hour:"2-digit", minute:"2-digit" }).toUpperCase().replace(",", " ·");
      // Re-examination lineage — stamped into the report jsonb so it persists
      // through save/load with no schema change.
      const lin = input.lineage;
      if (lin) {
        report._lineage = {
          origin_id: lin.origin_id,
          version: (lin.version || 1) + 1,
          prev_id: lin.prev_id,
          prev_drift: lin.prevDrift ?? null,
          prev_grade: lin.prevGrade ?? null,
        };
      }
      const entry = {
        id, ts,
        title: report.title || "Untitled Examination",
        input: { medium: input.medium, stance: input.stance, tags: input.tags, text: input.text },
        grade: report.grade,
        verdictClass: report.verdict_class === "pass" ? "clean" : report.verdict_class === "fail" ? "corrosive" : "mixed",
        verdictHeadline: report.verdict_headline,
        report,
      };
      setArchive(a => [entry, ...a]);
      setCurrent(entry);
      track("Examination Complete", { signedIn: !!getSessionToken(), grade: report.grade });
      setTimeout(() => setRoute("findings"), 350);

      // A credit was spent server-side; reflect the new balance locally.
      if (getSessionToken()) {
        setCredits(c => Math.max(0, c - 1));
        saveExamination(entry);
        fetchCredits().then(c => { if (typeof c === "number") setCredits(c); });
      }
    } catch (e) {
      clearInterval(tick);
      if (examSoundStop) examSoundStop();
      const code = e?.code || e?.message;
      if (code === "no_credits" || e.message === "__NO_CREDITS__") {
        // Signed-in user out of credits — show their exact balance.
        if (typeof e.credits === "number") setCredits(e.credits);
        setError(`Examination credits spent — you have ${typeof e.credits === "number" ? e.credits : 0} remaining. Acquire more to continue.`);
        setShowUpgrade(true);
        setRoute("bench");
      } else if (code === "limit_reached" || e.message === "__LIMIT_REACHED__") {
        // Anonymous free allowance exhausted → invite them to register
        // (a new account grants 5 fresh credits), not straight to paywall.
        setError(`You've used your ${e.limit ?? FREE_AUDIT_LIMIT} free examinations. Create a free account to receive 5 more credits and keep your archive.`);
        setRoute("bench");
        setAuthMode("signup");
        setShowAuth(true);
      } else if (code === "rate_limited") {
        setError(e.message);
        setRoute("intake");
      } else {
        setError(e.message || "Examination failed.");
        setRoute("intake");
      }
    }
  }, []);

  // ── Batch examination — run specimens sequentially (timeout-safe) ──
  const runBatch = useCallback(async (inputs) => {
    setError(null);
    setBatch({ total: inputs.length, done: 0, results: [], running: true, failed: 0 });
    setRoute("batch");

    const results = [];
    let failed = 0;
    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i];
      try {
        const report = await runAudit(input);
        const id = "AUD-" + new Date().toISOString().slice(2,10).replace(/-/g,"") + "-" + Math.random().toString(36).slice(2,5).toUpperCase();
        const ts = new Date().toLocaleString("en-GB", { day:"2-digit", month:"short", year:"2-digit", hour:"2-digit", minute:"2-digit" }).toUpperCase().replace(",", " ·");
        const entry = {
          id, ts,
          title: report.title || "Untitled Examination",
          input: { medium: input.medium, stance: input.stance, tags: input.tags, text: input.text },
          grade: report.grade,
          verdictClass: report.verdict_class === "pass" ? "clean" : report.verdict_class === "fail" ? "corrosive" : "mixed",
          verdictHeadline: report.verdict_headline,
          report,
        };
        results.push(entry);
        setArchive(a => [entry, ...a]);
        if (getSessionToken()) { setCredits(c => Math.max(0, c - 1)); saveExamination(entry); }
      } catch (e) {
        const code = e?.code || e?.message;
        // Out of credits / free limit → stop the batch cleanly, keep what we have.
        if (code === "no_credits" || code === "limit_reached" ||
            e.message === "__NO_CREDITS__" || e.message === "__LIMIT_REACHED__") {
          if (typeof e.credits === "number") setCredits(e.credits);
          setBatch(b => ({ ...b, done: results.length, results: [...results], running: false, failed, stopped: true }));
          if (getSessionToken()) fetchCredits().then(c => { if (typeof c === "number") setCredits(c); });
          return;
        }
        failed++;
      }
      setBatch(b => ({ ...b, done: i + 1, results: [...results], failed }));
    }

    setBatch(b => ({ ...b, done: inputs.length, results: [...results], running: false, failed }));
    if (getSessionToken()) fetchCredits().then(c => { if (typeof c === "number") setCredits(c); });
  }, []);

  return (
    <>
      {!unlocked && <GateScreen onUnlock={handleUnlock} />}
    <div className="shell">
      <RitualOverlay />

      <header className="topbar">
        <div className="brand" onClick={() => go("bench")} style={{ cursor: "pointer" }}>
          <span className="brand-mark"><BrandMark size={36} /></span>
          <span className="brand-name"><em>SPECVLVM</em>·Auditus</span>
        </div>
        <nav className="crumbs">
          <span className={route === "bench" ? "here" : ""} style={{cursor:"pointer"}} onClick={() => go("bench")}>Bench</span>
          <span className="sep">/</span>
          <span className={route === "intake" ? "here" : ""} style={{cursor:"pointer"}} onClick={() => go("intake")}>Intake</span>
          <span className="sep">/</span>
          <span className={route === "examination" ? "here" : ""}>Examination</span>
          <span className="sep">/</span>
          <span className={route === "findings" ? "here" : ""}>Findings</span>
          <span className="sep">/</span>
          <span className={route === "verdict" ? "here" : ""}>Verdict</span>
          <span className="sep">/</span>
          <span className={route === "archive" ? "here" : ""} style={{cursor:"pointer"}} onClick={() => go("archive")}>Archive</span>
          <span className="sep">/</span>
          <div className="codex-nav-wrap">
            <span className={route === "lore" ? "here" : ""} style={{cursor:"pointer"}} onClick={() => go("lore")}>Codex</span>
            <div className="codex-popover">
              <div className="codex-popover-title">THE CODEX · ORDO SEMEION</div>
              <div className="codex-popover-desc">
                The full lore, doctrine, and cosmological architecture of Field Instrument № 01. The Order's history. The Charter §§I–VI decoded. The Compiler. Five mapped outcomes.
              </div>
              <span className="codex-popover-link" onClick={() => go("lore")}>Enter the Codex →</span>
            </div>
          </div>
        </nav>
        <div className="topbar-meta">
          <span><span className="dot"></span>&nbsp;CALIBRATED</span>
          <span>ORDO·SEMEION</span>
          <span>MMXXVI</span>
          {signedIn && (
            <div className="topbar-examiner-badge" onClick={() => setShowUpgrade(true)} style={{cursor:"pointer"}}>
              <div className="topbar-examiner-dot"/>
              {credits} CREDIT{credits === 1 ? "" : "S"}
            </div>
          )}
          {account ? (
            <button className={`topbar-auth-btn ${credits>0?"examiner":""}`} onClick={() => setShowAccount(true)}>
              {account.email?.split("@")[0]}
            </button>
          ) : (
            <button className="topbar-auth-btn" onClick={() => { setAuthMode("signin"); setShowAuth(true); }}>
              Sign In
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="error-banner" onClick={() => setError(null)}>
          <span className="error-banner-icon">⚠</span>
          <span className="error-banner-text">{error}</span>
          <span className="error-banner-dismiss">DISMISS ✕</span>
        </div>
      )}

      {route === "bench" && <Bench go={go} archive={archive} signedIn={signedIn} credits={credits} onUpgrade={() => setShowUpgrade(true)} />}
      {route === "intake" && <Intake go={go} run={run} runBatch={runBatch} preload={preload} credits={credits} signedIn={signedIn} />}
      {route === "batch" && <BatchScreen batch={batch} go={go} onOpen={(entry) => go("findings", entry)} onCompare={(a,b) => setComparePair({a,b})} />}
      {route === "examination" && <Examination progress={progress} />}
      {route === "findings" && <Findings data={current} go={go} isExaminer={signedIn} onUpdateNotes={updateNotes} onShare={createShareLink} onRevokeShare={revokeShareLink} />}
      {route === "verdict" && <Verdict data={current} go={go} isExaminer={signedIn} onExportPDF={() => exportPDF({ report: current?.report, input: current?.input, id: current?.id, ts: current?.ts })} />}
      {route === "archive" && <Archive archive={archive} go={go} isExaminer={signedIn} onDelete={deleteExamination} setArchive={setArchive} onCompare={(a,b) => setComparePair({a,b})} onSetCollection={setCollection} />}
      {route === "lore" && <Lore go={go} />}
      {route === "legal" && <LegalScreen go={go} section={legalSection} />}
      {showUpgrade && <UpgradeScreen onClose={() => setShowUpgrade(false)} onCheckout={initiateCheckout} onPayPal={initiatePayPal} packs={CREDIT_PACKS} signedIn={signedIn} credits={credits} onRequireAuth={() => { setShowUpgrade(false); setAuthMode("signup"); setShowAuth(true); }} />}
      {showRestore && <RestoreScreen onClose={() => setShowRestore(false)} onRequest={requestRestore} />}
      {sharedEntry && <SharedExaminationViewer entry={sharedEntry} onClose={() => setSharedEntry(null)} go={go} />}
      {comparePair && <CompareViewer a={comparePair.a} b={comparePair.b} onClose={() => setComparePair(null)} />}
      {shareCardData && <ShareCard data={shareCardData} onClose={() => setShareCardData(null)} />}
      {showAuth && <AuthScreen mode={authMode} onModeChange={setAuthMode} onSignIn={handleSignIn} onSignUp={handleSignUp} onReset={(email) => authOp("reset-password", { email })} onClose={() => setShowAuth(false)} />}
      {recoveryToken && <NewPasswordScreen
        onSubmit={(pw) => setNewPassword(recoveryToken, pw)}
        onDone={() => {
          setRecoveryToken(null);
          window.history.replaceState({}, "", window.location.pathname);
          setAuthMode("signin"); setShowAuth(true);
        }} />}
      {showAccount && <AccountScreen account={account} credits={credits} onSignOut={handleSignOut} onClose={() => setShowAccount(false)} onUpgrade={() => { setShowAccount(false); setShowUpgrade(true); }} onFetchProfile={fetchProfile} onFetchPurchases={fetchPurchases} onExportData={exportMyData} onDeleteAccount={deleteMyAccount} />}
      {creditsGranted > 0 && (
        <div className="examiner-granted-toast" onClick={() => setCreditsGranted(0)}>
          <span className="examiner-granted-mark">✓</span>
          <div>
            <div className="examiner-granted-title">{creditsGranted} CREDITS ADDED</div>
            <div className="examiner-granted-sub">The bench is yours. Credits do not expire. The Charter remains.</div>
          </div>
        </div>
      )}

      <footer className="specbar">
        <div className="left">
          <span>SPECULUM · Rev. III</span>
          <span style={{ color: "var(--ink-dim)" }}>Nothing retained unless you save it</span>
          <span className="specbar-link" style={{cursor:"pointer"}} onClick={() => { setLegalSection("terms"); go("legal"); }}>Terms</span>
          <span className="specbar-link" style={{cursor:"pointer"}} onClick={() => { setLegalSection("refund"); go("legal"); }}>Refunds</span>
          <a className="specbar-link" href="mailto:initiate@ra999.info">Support</a>
        </div>
        <div className="right">
          <span>ORDO·SEMEION·MMXXVI</span>
        </div>
      </footer>

      {/* Tweaks panel */}
      <TweaksPanel title="Tweaks">
        <TweakSection label="Palette">
          <TweakSelect
            label="Tone"
            value={t.palette}
            onChange={v => setTweak("palette", v)}
            options={[
              { value: "ink",       label: "Ink" },
              { value: "parchment", label: "Cream" },
              { value: "verdigris", label: "Verde" },
              { value: "seraphic",  label: "Angelic Vampire" },
            ]}
          />
        </TweakSection>
        <TweakSection label="Density">
          <TweakRadio
            label="Spacing"
            value={t.density}
            onChange={v => setTweak("density", v)}
            options={[
              { value: "regular", label: "Regular" },
              { value: "loose",   label: "Loose" },
            ]}
          />
        </TweakSection>
        <TweakSection label="Ornamentation">
          <TweakRadio
            label="Flourish"
            value={t.ornament}
            onChange={v => setTweak("ornament", v)}
            options={[
              { value: "none",    label: "None" },
              { value: "light",   label: "Light" },
              { value: "baroque", label: "Baroque" },
            ]}
          />
        </TweakSection>
        <TweakSection label="Ritual Layer">
          <TweakToggle
            label="Bg sigil"
            value={t.sigilOverlay}
            onChange={v => setTweak("sigilOverlay", v)}
          />
        </TweakSection>
      </TweaksPanel>
    </div>
    </>
  );
}

/* ---------- Error boundary ----------
   Catches any runtime render error so the app degrades to a calm notice
   instead of a blank white screen. */
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error("App error:", error?.message, info?.componentStack);
    reportError(error, { componentStack: info?.componentStack });
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
          background: "var(--bg, #150a0e)", color: "var(--ink, #ece4d2)",
          fontFamily: "'Cormorant Garamond', Georgia, serif", textAlign: "center", padding: "32px",
        }}>
          <div style={{ maxWidth: "44ch" }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.2em",
              textTransform: "uppercase", color: "var(--ink-faint, #6e6757)", marginBottom: 16 }}>
              ORDO · SEMEION · INSTRUMENT FAULT
            </div>
            <h1 style={{ fontSize: 30, fontStyle: "italic", fontWeight: 400, marginBottom: 14 }}>
              The instrument has stalled.
            </h1>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: "var(--ink-dim, #b4ac99)", marginBottom: 24 }}>
              An unexpected fault interrupted the examination. Your saved work is safe.
              Reload the bench to continue.
            </p>
            <button onClick={() => window.location.reload()} style={{
              background: "transparent", border: "1px solid var(--accent, #c9a570)", color: "var(--accent, #c9a570)",
              padding: "12px 28px", cursor: "pointer", fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
            }}>
              Reload the Bench
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <ErrorBoundary><App /></ErrorBoundary>
);
